import React, {
  useState,
  useEffect,
  forwardRef,
  useRef,
  useContext,
} from "react";
import { AuthContext } from "./AuthContextComponent";

interface TimerProps {
  duration: number;
  onFinish?: () => void;
}

const Timer = forwardRef<any, TimerProps>(({ duration, onFinish }, ref) => {
  const [secondsRemaining, setSecondsRemaining] = useState(duration);
  const workerRef = useRef<Worker | null>(null);
  const userInfo = useContext(AuthContext);

  useEffect(() => {
    if (duration <= 0) return; // 👈 Skip if duration is 0 or negative

    // Inline Web Worker code
    const workerBlob = new Blob(
      [
        `
      let duration = 0;
      let intervalId = null;

      self.onmessage = (event) => {
        if (event.data.type === 'start') {
          duration = event.data.duration;
          intervalId = setInterval(() => {
            duration -= 1;
            if (duration >= 0) {
              self.postMessage({ secondsRemaining: duration });
            } else {
              clearInterval(intervalId);
              self.postMessage({ finished: true });
            }
          }, 1000);
        } else if (event.data.type === 'stop') {
          clearInterval(intervalId);
        }
      };
    `,
      ],
      { type: "application/javascript" }
    );

    workerRef.current = new Worker(URL.createObjectURL(workerBlob));

    // Start the timer
    workerRef.current.postMessage({ type: "start", duration });

    // Listen for messages from the worker
    workerRef.current.onmessage = (event) => {
      if (event.data.finished) {
        if (onFinish) onFinish();
      } else {
        setSecondsRemaining(event.data.secondsRemaining);
      }
    };

    // Cleanup function to terminate the worker
    return () => {
      if (workerRef.current) {
        workerRef.current.postMessage({ type: "stop" });
        workerRef.current.terminate();
      }
    };
  }, [duration]);

  const color = userInfo.userInfo?.theme === "DARK" ? "white" : "black";
  const minutes = String(Math.floor(secondsRemaining / 60)).padStart(1, "0");
  const seconds = String(secondsRemaining % 60).padStart(2, "0");
  const formattedTime = `${minutes}:${seconds}`;

  return (
    <div className="flex justify-center items-center -mt-5 w-full">
      <h1
        className="flex md:text-lg text-custom-petite font-mono text-center  gap-2"
        style={{ color }}
      >
        {formattedTime}
      </h1>
    </div>
  );
});

export default Timer;
